import { useRef, useState } from 'react';
import { Alert, Box, Button, Card, FileButton, Group, Stack, Switch, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconPhoto, IconUpload, IconX } from '@tabler/icons-react';
import { HOME_BACKGROUND_COPY as C, HOME_IMAGE_BRIGHT_CODE, type TvPlatformKey } from '@/lib/constants/appFactory';
import type { AppCfgsDto } from '@/lib/types/App';
import type { FactoryBrandKit } from '@/lib/types/AppFactory';
import { ApiError } from '@/shared/api/ApiError';
import { useResellerBrand } from '@/shared/hooks/useResellerBrand';
import { AppPreviewUtils } from '../AppPreviewUtils';
import { BrandKitUtils } from '../BrandKitUtils';
import { HomeBackgroundUtils } from '../HomeBackgroundUtils';
import { useSaveHomeImage } from '../useAppFactory';
import { useSetHomeBackground } from '../useAppMutations';
import { useAppPreviewBrowse, useAppPreviewCfg } from '../useAppPreview';
import { AppTvFrame } from './AppTvFrame';

interface AppHomeBackgroundProps {
  appCfgId: number;
  app: AppCfgsDto;
  /** The app's newest Brand Kit revision; null = none. */
  kit: FactoryBrandKit | null;
  /** The TV platforms that are on (the help names the ones that draw the image behind their icon; the preview asks for the first one's payload). */
  tv: readonly TvPlatformKey[];
  /** The kit's quality report says the background image is bright (`home_image_bright`): the text over it may be hard to read. */
  bright?: boolean;
  disabled?: boolean;
}

const PREVIEW_MAX_WIDTH = 480;

/**
 * The home background card (3.46.0; appfactory ADR-0052; design ADR-0016 D5):
 * the Background image, saved ON ITS OWN — a pick and a Save store it in the
 * Brand Kit and render the picture at once, no TV render, nothing to approve
 * — and the switch that shows it as the home screen's picture on every app
 * of the account (the Web App and the TV apps alike), AS UPLOADED, no
 * darkening, no blur; the featured title's text sits on its left side over
 * the dark gradient and the videos appear in rows below. Beside them, the
 * home screen drawn with the image, so the result is seen BEFORE the switch
 * is on; a bright image gets the factory's warning line (the customer
 * carries the result; nothing is gated). Anyone who can edit the app can
 * set both: customer, reseller, operator. The Fire TV / Google TV renders
 * pick the image up behind their icon at the next Generate.
 */
export function AppHomeBackground({ appCfgId, app, kit, tv, bright = false, disabled = false }: AppHomeBackgroundProps) {
  const isReseller = useResellerBrand();
  const switchMutation = useSetHomeBackground(appCfgId);
  const saveMutation = useSaveHomeImage(appCfgId);
  const view = HomeBackgroundUtils.view({
    core: app.appCfg.core,
    kit,
    lightTheme: HomeBackgroundUtils.isLightTheme(app),
    isReseller,
  });
  // The switch mutation stays pending until the app is re-read, so its value stands in for the stored one meanwhile.
  const checked = switchMutation.isPending ? switchMutation.variables.enabled : view.checked;

  // The staged change: a picked file, or the stored image marked for removal.
  const [picked, setPicked] = useState<File | null>(null);
  const [pickedUrl, setPickedUrl] = useState<string | null>(null);
  const [pickAdvice, setPickAdvice] = useState<string[]>([]);
  const [removing, setRemoving] = useState(false);
  const [savedBright, setSavedBright] = useState(false);
  const pickSeq = useRef(0);
  const resetPick = useRef<() => void>(null);

  const clearPick = () => {
    pickSeq.current++;
    resetPick.current?.();
    if (pickedUrl) URL.revokeObjectURL(pickedUrl);
    setPicked(null);
    setPickedUrl(null);
    setPickAdvice([]);
  };

  const pick = async (file: File | null) => {
    if (!file) return;
    if (file.size > BrandKitUtils.MAX_LOGO_BYTES) {
      notifications.show({ title: 'File too large', message: 'Images must be under 8MB.', color: 'red' });
      return;
    }
    const seq = ++pickSeq.current;
    if (pickedUrl) URL.revokeObjectURL(pickedUrl);
    setPicked(file);
    setPickedUrl(URL.createObjectURL(file));
    setRemoving(false);
    // the Home shows the picture as uploaded (design ADR-0016 D5): a bright one gets the factory's warning now, not after the save
    const px = await BrandKitUtils.readImagePixels(file);
    if (seq !== pickSeq.current) return;
    setPickAdvice(BrandKitUtils.backgroundAdvice(px ? BrandKitUtils.meanBrightness(px.rgba) : null));
  };

  const save = async () => {
    let base64: string | null = null;
    if (!removing) {
      if (!picked) return;
      try {
        base64 = await BrandKitUtils.fileToBase64(picked);
      } catch (err) {
        notifications.show({ title: 'Error', message: err instanceof Error ? err.message : 'Could not read the image.', color: 'red' });
        return;
      }
    }
    saveMutation.mutate(base64, {
      onSuccess: (res) => {
        clearPick();
        setRemoving(false);
        const isBright = (res.homePicture?.warnings ?? []).includes(HOME_IMAGE_BRIGHT_CODE);
        setSavedBright(isBright);
        notifications.show({
          title: 'Saved',
          message: base64 == null ? C.imageRemoved : view.checked ? C.imageSavedOn : C.imageSavedOff,
          color: 'green',
        });
        if (isBright) notifications.show({ title: 'Check this image', message: C.bright, color: 'yellow' });
      },
      onError: (err) =>
        BrandKitUtils.isStillWorking(err)
          ? notifications.show({ title: 'Still working', message: 'Saving the image is taking longer than usual. It appears here in a moment.', color: 'blue' })
          : notifications.show({
              title: 'Error',
              message: err instanceof ApiError ? BrandKitUtils.refusalMessage(err, {}) : err.message,
              color: 'red',
            }),
    });
  };

  const setSwitch = (enabled: boolean) => {
    switchMutation.mutate(
      { app, enabled },
      {
        onSuccess: (res) => {
          // An api that does not know the setting answers 200 and stores nothing: say so instead of a silent flip back.
          const stored = res?.appCfgDto?.appCfg?.core;
          if (stored && (stored.homeBgEnabled ?? false) !== enabled) {
            notifications.show({ title: 'Not saved', message: C.notAvailable, color: 'orange' });
            return;
          }
          notifications.show({ title: 'Saved', message: enabled ? C.savedOn : C.savedOff, color: 'green' });
        },
        onError: (err) =>
          notifications.show({
            title: 'Error',
            message: err.message?.includes('409') || err.message?.includes('conflict') ? C.conflict : err.message,
            color: 'red',
          }),
      },
    );
  };

  const stored = view.imageUrl != null && !removing;
  const thumbUrl = pickedUrl ?? (stored ? view.imageUrl : null);
  const staged = picked != null || removing;
  const busy = saveMutation.isPending;
  const showBright = (bright || savedBright) && view.kind !== 'none' && !picked;

  return (
    <Card withBorder radius="md" p="md" data-testid="home-background" data-home-bg-kind={view.kind}>
      <Stack gap="md">
        <Group gap="xs">
          <IconPhoto size={20} />
          <Text fw={600} size="md">
            {C.title}
          </Text>
        </Group>
        <Text size="sm" c="dimmed">
          {C.intro}
        </Text>

        <Group align="flex-start" gap="xl" wrap="wrap">
          <Stack gap="sm" style={{ flex: '1 1 320px', minWidth: 0 }} maw={520}>
            {/* The image: what is stored (a thumbnail), a newly picked file over it, replace / remove, and Save. */}
            <Stack gap={4} data-testid="home-background-image-slot">
              <Text size="sm" fw={500}>
                {C.imageLabel}
              </Text>
              <Group gap="sm" align="center" wrap="wrap">
                {thumbUrl && (
                  <Box
                    style={{
                      width: 128,
                      height: 72,
                      flex: 'none',
                      borderRadius: 6,
                      border: '1px solid var(--mantine-color-gray-3)',
                      background: 'var(--mantine-color-dark-8)',
                      overflow: 'hidden',
                    }}
                  >
                    <img src={thumbUrl} alt={C.imageLabel} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                  </Box>
                )}
                <FileButton resetRef={resetPick} onChange={(f) => void pick(f)} accept="image/png,image/jpeg">
                  {(props) => (
                    <Button variant="default" leftSection={<IconUpload size={14} />} disabled={disabled || busy} {...props}>
                      {picked ? picked.name : stored ? C.imageReplace : C.imageChoose}
                    </Button>
                  )}
                </FileButton>
                {picked ? (
                  <Button variant="subtle" size="compact-sm" color="gray" leftSection={<IconX size={12} />} onClick={clearPick} disabled={busy}>
                    {view.imageUrl ? C.imageKeep : 'Clear'}
                  </Button>
                ) : stored ? (
                  <Button variant="subtle" size="compact-sm" color="gray" leftSection={<IconX size={12} />} onClick={() => setRemoving(true)} disabled={disabled || busy}>
                    {C.imageRemove}
                  </Button>
                ) : removing ? (
                  <Button variant="subtle" size="compact-sm" onClick={() => setRemoving(false)} disabled={busy}>
                    {C.imageKeep}
                  </Button>
                ) : null}
                {!picked && stored && (
                  <Text size="xs" c="dimmed">
                    {C.imageStored}
                  </Text>
                )}
              </Group>
              <Text size="xs" c="dimmed">
                {HomeBackgroundUtils.imageHelp(tv)}
              </Text>
              {pickAdvice.length > 0 && (
                <Alert icon={<IconAlertCircle size={16} />} color="yellow" py={6}>
                  {pickAdvice.map((a) => (
                    <Text size="sm" key={a}>
                      {a}
                    </Text>
                  ))}
                </Alert>
              )}
              {staged && (
                <Group>
                  <Button size="sm" onClick={() => void save()} loading={busy} disabled={disabled} color={removing ? 'red' : undefined} data-testid="home-background-save">
                    {removing ? C.imageSaveRemoval : C.imageSave}
                  </Button>
                </Group>
              )}
            </Stack>

            <Switch
              label={C.switchLabel}
              description={C.switchHelp}
              checked={checked}
              disabled={disabled || view.disabled || switchMutation.isPending}
              onChange={(e) => setSwitch(e.currentTarget.checked)}
            />
            {view.note && (
              <Text size="sm" c={view.disabled ? 'dimmed' : undefined} data-testid="home-background-note">
                {view.note}
              </Text>
            )}
            {showBright && (
              <Alert icon={<IconAlertCircle size={16} />} color="yellow" py={6} data-testid="home-background-bright">
                <Text size="sm">{C.bright}</Text>
              </Alert>
            )}
          </Stack>

          {view.previewUrl && !removing && <HomeBackgroundPreview app={app} tv={tv} url={pickedUrl ?? view.previewUrl} />}
        </Group>
      </Stack>
    </Card>
  );
}

interface HomeBackgroundPreviewProps {
  app: AppCfgsDto;
  tv: readonly TvPlatformKey[];
  url: string;
}

/**
 * The app's real home screen (its live cfg + browse) with the background
 * image as its picture, switch on or off — a newly picked file is drawn
 * before it is saved. An app whose address is not set up yet, or whose
 * payload cannot be read, shows the image on its own.
 */
function HomeBackgroundPreview({ app, tv, url }: HomeBackgroundPreviewProps) {
  const appDomain = AppPreviewUtils.appDomain(app);
  const platform = tv[0] ?? 'roku';
  const cfgQuery = useAppPreviewCfg(appDomain, platform);
  const browseQuery = useAppPreviewBrowse(appDomain, platform);
  const cfg = cfgQuery.data;
  const browse = browseQuery.data;

  if (cfg && browse) {
    return (
      <Stack gap={6} style={{ flex: `1 1 ${PREVIEW_MAX_WIDTH}px`, minWidth: 0 }} maw={PREVIEW_MAX_WIDTH} data-testid="home-background-preview">
        <AppTvFrame cfg={cfg} browse={browse} maxWidth={PREVIEW_MAX_WIDTH} backdropUrl={url} />
        <Text size="xs" c="dimmed">
          {C.previewCaption}
        </Text>
      </Stack>
    );
  }
  return (
    <Stack gap={6} style={{ flex: `1 1 ${PREVIEW_MAX_WIDTH}px`, minWidth: 0 }} maw={PREVIEW_MAX_WIDTH} data-testid="home-background-image">
      <div
        style={{
          width: '100%',
          aspectRatio: '16 / 9',
          overflow: 'hidden',
          borderRadius: 6,
          border: '1px solid var(--mantine-color-gray-3)',
          background: 'var(--mantine-color-dark-8)',
        }}
      >
        <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
      </div>
      <Text size="xs" c="dimmed">
        {C.imageCaption}
      </Text>
    </Stack>
  );
}
